import { format, isValid } from "date-fns";
import { CompetitionStatus } from "../api/useCompetitions";
import { BEGINNING_STATISTICS_DATE, MONTHS_SHORT } from "./constants";
import { dateFnsLocale, fetchLang, svLocale } from "./language.helpers";

export const locale = () => dateFnsLocale();

export function formatSecondsToHours(durationInSeconds?: number) {
  if (durationInSeconds === undefined) return null;
  const h = svLocale() ? "tim" : "h";
  const hours = Math.floor(durationInSeconds / 3600);
  return hours + " " + h;
}

export function formatRuntime(secondsWithDecimals: number) {
  // Whole hundredths, truncated like a race clock. Rounding to 1e-4 first
  // strips float noise: 90.1 - 90 is 0.0999…, which used to print as .09.
  const cs = Math.floor(Math.round(secondsWithDecimals * 1e4) / 100);
  const h = Math.floor(cs / 360000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const mmss = `${pad(Math.floor(cs / 6000) % 60)}:${pad(Math.floor(cs / 100) % 60)}`;

  return `${h ? `${pad(h)}:` : ""}${mmss}.${pad(cs % 100)}`;
}

export function formatSecondsToReadable(
  durationInSeconds?: number,
  showSeconds?: boolean,
  language?: string,
) {
  if (durationInSeconds === undefined) return null;

  const duration = {
    hours: Math.floor(durationInSeconds / 3600),
    minutes:
      Math.floor(durationInSeconds / 3600) < 1 && !showSeconds
        ? Math.ceil((durationInSeconds % 3600) / 60)
        : Math.floor((durationInSeconds % 3600) / 60),
    seconds: Math.floor(durationInSeconds % 60),
  };

  const hours = duration.hours;
  const minutes = duration.minutes;
  const h = svLocale() && !language?.includes("en") ? "tim" : "h";
  const m = "min";
  const mDisplay =
    showSeconds && duration.minutes === 0 ? "" : `${minutes} ${m}`;
  const sDisplay = duration.seconds + " s";
  const hDisplay = hours > 0 ? `${hours.toLocaleString(fetchLang())} ${h}` : "";
  return `${hDisplay} ${mDisplay} ${showSeconds ? sDisplay : ""}`;
}

export function formatDateToReadable(
  date?: Date | string,
  dateFormat: string = "EEEE dd LLL HH:mm",
  fallback = "–",
) {
  if (!date) return fallback;
  const d = typeof date === "string" ? new Date(safariDateFix(date)) : date;
  if (!isValid(d)) return fallback;
  return format(d, dateFormat, { locale: locale() });
}

export function formatTimestamp(
  t: string | Date | null | undefined,
  formatString: "date" | "time" | "dateAndTime" | { custom: string },
  fallback = "–",
) {
  if (!t) return fallback;
  const timestamp = typeof t === "string" ? safariDateFix(t) : t;
  if (!isValid(new Date(timestamp))) return fallback;
  let formatStr = "";
  if (typeof formatString === "string") {
    switch (formatString) {
      case "date":
        formatStr = "PP";
        break;
      case "time":
        formatStr = "p";
        break;
      case "dateAndTime":
        formatStr = "PP p";
        break;
    }
  } else {
    formatStr = formatString.custom;
  }

  return format(timestamp, formatStr, { locale: locale() });
}

export const dateStringToReadable = (
  date: string,
  alwaysShowYear = false,
  fallback?: string,
) =>
  date
    ? dateObjectToReadable(new Date(safariDateFix(date)), alwaysShowYear)
    : (fallback ?? "");

const today = new Date();
export const dateObjectToReadable = (date: Date, alwaysShowYear = false) =>
  `${date.getDate()} ${MONTHS_SHORT()[date.getMonth()]}${
    today.getFullYear() > date.getFullYear() || alwaysShowYear
      ? ` ${date.getFullYear()}`
      : ""
  }`;
export const dateObjectToReadableWithYear = (date: Date) =>
  dateObjectToReadable(date, true);

export function formatLastActive(lastActive?: string, fallback?: string) {
  if (!lastActive || lastActive === "") return fallback ?? "-";

  return dateObjectToReadable(new Date(lastActive), true);
}

const replaceAt = (string: string, index: number, replacement: string) => {
  if (index >= string.length) return string;
  return string.substring(0, index) + replacement + string.substring(index + 1);
};

export const safariDateFix = (timestamp: string) =>
  timestamp.charAt(10) === " " ? replaceAt(timestamp, 10, "T") : timestamp;

export function convertDateToYYYYMMDD(date: Date) {
  var d = new Date(date),
    month = "" + (d.getMonth() + 1),
    day = "" + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}

export function formatHighscoreRuntime(secondsWithDecimals: number) {
  const h = Math.floor(secondsWithDecimals / 3600);
  const m = Math.floor((secondsWithDecimals % 3600) / 60);
  const s = Math.floor((secondsWithDecimals % 3600) % 60);

  const hDisplay = h < 1 ? "" : h < 10 ? "0" + h : h;
  const mDisplay = m < 10 ? "0" + m : m;
  const sDisplay = s < 10 ? "0" + s : s;

  return `${hDisplay ? `${hDisplay}:` : ""}${mDisplay}:${sDisplay}`;
}

export const dateDifferenceInDays = (date1: Date, date2: Date) =>
  Math.round((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));

export const addOneDayToDate = (date: Date) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + 1);
  return nextDate;
};

export function getCompetitionStatus({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}): CompetitionStatus {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (now.getTime() > start.getTime() && now.getTime() < end.getTime())
    return "active";

  if (now.getTime() > end.getTime()) return "past";

  return "coming";
}

export function getBeginningStatisticsDate() {
  const beginning = new Date(BEGINNING_STATISTICS_DATE);
  beginning.setHours(0, 0, 0, 0);
  return beginning;
}

export function getToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function getDateOneYearBeforeToday() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date;
}

export function getDateOneYearFromToday() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

export function getMondayOfThisWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function getSundayOfThisWeek() {
  const sunday = getMondayOfThisWeek();
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(0, 0, 0, 0);
  return sunday;
}

export function getFirstDayOfThisMonth() {
  const firstDay = new Date();
  firstDay.setDate(1);
  firstDay.setHours(0, 0, 0, 0);
  return firstDay;
}

export function getLastDayOfThisYear() {
  const lastDay = new Date();
  lastDay.setMonth(11);
  lastDay.setDate(31);
  lastDay.setHours(0, 0, 0, 0);
  return lastDay;
}

export function getLastDayOfThisMonth() {
  const lastDay = new Date();
  lastDay.setDate(1);
  lastDay.setMonth(lastDay.getMonth() + 1);
  lastDay.setDate(lastDay.getDate() - 1);
  lastDay.setHours(0, 0, 0, 0);
  return lastDay;
}

export function datesAreOnSameDay(a: Date, b: Date | null) {
  if (b === null) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isTimestampOverdue(timestamp: string) {
  const timestampDate = new Date(timestamp);

  if (isNaN(timestampDate.getTime())) {
    console.error(`Ogiltigt datumformat: "${timestamp}"`);
    return false;
  }
  const now = new Date();
  return timestampDate < now;
}
